/**
 * @file authContext.ts
 * @brief The auth context object, its value shape, and the `useAuth` hook.
 *
 * @details
 * Split out of `AuthContext.tsx` (02-08-2026). `AuthProvider` is a component;
 * a module that exports **both** a component and a value (a hook is a value)
 * loses React Fast Refresh — `@vitejs/plugin-react` can no longer hot-swap it,
 * so every edit to the provider full-reloaded the whole panel. Context, types
 * and hook live here; the provider stays in the `.tsx`.
 *
 * Import `useAuth` from here; import `AuthProvider` from `AuthContext.tsx`.
 * `AuthUserSchema` is **not** re-exported — import it from
 * `@/shared/types/api`, which is where it is defined.
 *
 * @dependencies
 * - shared/types/api (AuthUser, Entitlements)
 *
 * @references
 * - shared/auth/AuthContext.tsx
 *
 * @author DES
 * @date 02-08-2026
 */

import { createContext, useContext } from "react";
import type { AuthUser, Entitlements } from "@/shared/types/api";
import type { TwoFactorChallenge } from "@/features/auth/twoFactor/twoFactorRules";

export type AuthStatus =
  | "idle"
  | "loading"
  | "authenticated"
  | "unauthenticated"
  /** The token is kept but /me could not be reached (backend restarting,
   *  5xx, timeout, offline): retrying, the app shows "Reconnecting…". */
  | "reconnecting";

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface AppleLoginBody {
  identityToken: string;
  firstName?: string;
  lastName?: string;
}

/** POST /api/auth/2fa/setup/start (B31): what the QR step shows. */
export interface TwoFactorSetupInfo {
  otpauthUri: string;
  /** PNG data URL of the QR code. */
  qrCodeDataUrl: string;
  /** The secret in groups ("ABCD EFGH …") for typing into the app. */
  setupKey: string;
  issuer?: string;
  accountName?: string;
  digits?: number;
  period?: number;
}

export interface TwoFactorProof {
  code?: string;
  backupCode?: string;
  /** "Trust this device for 30 days"; ignored when not allowed (the Owner). */
  trustDevice?: boolean;
}

export interface SignInOptions {
  /**
   * Signing in again to confirm a delete (B33): the screen stays mounted and
   * a failed or 2FA answer keeps the current session.
   */
  reauth?: boolean;
}

export interface AuthContextValue {
  status: AuthStatus;
  user: AuthUser | null;
  entitlements: Entitlements;
  token: string | null;
  login: (credentials: LoginCredentials) => Promise<AuthUser>;
  loginWithGoogle: (idToken: string, opts?: SignInOptions) => Promise<AuthUser>;
  loginWithApple: (body: AppleLoginBody, opts?: SignInOptions) => Promise<AuthUser>;
  /** Sign in with Microsoft: a Microsoft ID token (06-10-2026). */
  loginWithMicrosoft: (idToken: string, opts?: SignInOptions) => Promise<AuthUser>;
  /**
   * A one-tap link from a billing email (18-10-2026): signs in as the link's
   * user and returns where it should open (an /app/ path from the server).
   */
  loginWithMagicLink: (token: string) => Promise<{ user: AuthUser; redirect: string | null }>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<AuthUser | null>;
  /**
   * Two-step verification (B31, 25-10-2026). Set when the last sign-in
   * answered TWO_FACTOR_REQUIRED (the code screen) or
   * TWO_FACTOR_SETUP_REQUIRED (the setup-only screen). Held here, not in the
   * page, so the login page and the one-tap link page share it (the login
   * page no longer unmounts while the first factor runs, W27). Memory only,
   * never stored.
   */
  twoFactorChallenge: TwoFactorChallenge | null;
  /** "Back to sign in": drops the challenge. */
  cancelTwoFactor: () => void;
  /** POST /2fa/verify with the pending token; signs in on success. */
  verifyTwoFactor: (
    proof: TwoFactorProof,
  ) => Promise<{ user: AuthUser; backupCodesLeft: number | null; redirect: string | null }>;
  /** Setup-only screen, step 1: POST /2fa/setup/start with the setup token. */
  startChallengeSetup: () => Promise<TwoFactorSetupInfo>;
  /**
   * Setup-only screen, step 2: POST /2fa/setup/confirm. Returns the backup
   * codes and `finish`, which starts the session once the user has saved
   * them (signing in first would leave the page before they are shown).
   */
  confirmChallengeSetup: (
    code: string,
  ) => Promise<{ backupCodes: string[]; redirect: string | null; finish: () => Promise<AuthUser> }>;
}

export const AuthContext = createContext<AuthContextValue | undefined>(
  undefined,
);

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within <AuthProvider>");
  }
  return ctx;
}
